import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Grid,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  buildPreviewUrlWithPresignedDocumentUrl,
  getBundleExplorerUrl,
} from "@metriport/shared-internal";
import { FiDownload, FiExternalLink } from "react-icons/fi";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { S3BucketRow } from "../../api/schemas/s3-bucket";
import type { ObjectOverviewResponse } from "../../api/schemas/s3-object-overview";
import type { S3ObjectEntry } from "../../api/schemas/s3-bucket-objects";
import {
  downloadBucketObject,
  fetchBucketObjectDownloadInfo,
  getObjectOverview,
  listBucketObjects,
  listBuckets,
} from "../../api/s3-buckets";
import { getErrorMessageInternal } from "../../shared/error";
import { Table } from "../shared/Table";
import { showBundleExplorerForS3ObjectKey } from "./bundle-explorer-s3-rules";

const BUCKET_TABLE_COLUMNS = ["Bucket", "Created"] as const;
const BUCKET_CONTENT_COLUMNS = ["Name", "Size", "Last modified"] as const;

function formatCreatedAt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function formatSize(bytes: number | undefined): string {
  if (bytes === undefined) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/** S3 returns ETags wrapped in double quotes; strip for display. */
function formatEtagForDisplay(raw: string | undefined): string {
  if (raw === undefined || raw === "") return "—";
  const stripped = raw.trim().replace(/^"+|"+$/g, "");
  return stripped === "" ? "—" : stripped;
}

function overviewDetailRows(
  data: ObjectOverviewResponse,
  listRow: S3ObjectEntry
): [string, string][] {
  const key = data.key ?? listRow.key;
  const lastModifiedRaw = data.lastModified ?? listRow.lastModified;
  const lastModified = lastModifiedRaw ? formatCreatedAt(lastModifiedRaw) : "—";
  const sizeBytes =
    typeof data.sizeInBytes === "number"
      ? data.sizeInBytes
      : typeof data.size === "number"
      ? data.size
      : listRow.size;
  const sizeDisplay =
    typeof data.size === "string" && data.size.length > 0 ? data.size : formatSize(sizeBytes);
  const contentType = data.contentType ?? data.type ?? "—";
  const etag = formatEtagForDisplay(data.eTag ?? data.etag);
  const httpsUrl = data.httpsUrl ?? data.uri ?? "—";
  const s3Uri = data.s3Uri ?? "—";
  return [
    ["Key", key],
    ["Bucket", data.bucket ?? "—"],
    ["Region", data.region ?? "—"],
    ["ARN", data.arn ?? "—"],
    ["Size", sizeDisplay],
    ["Content-Type", contentType],
    ["Last modified", lastModified],
    ["HTTPS URL", httpsUrl],
    ["S3 URI", s3Uri],
    ["ETag", etag],
  ];
}

function folderDisplayName(fullPrefix: string): string {
  const trimmed = fullPrefix.replace(/\/$/, "");
  const segments = trimmed.split("/").filter(Boolean);
  const last = segments[segments.length - 1] ?? trimmed;
  return `${last}/`;
}

function breadcrumbFolderLabel(fullPrefix: string): string {
  return folderDisplayName(fullPrefix).replace(/\/$/, "");
}

function cumulativePrefixes(fullPrefix: string): string[] {
  if (!fullPrefix) return [];
  const parts = fullPrefix.replace(/\/$/, "").split("/").filter(Boolean);
  const out: string[] = [];
  let acc = "";
  for (const p of parts) {
    acc += `${p}/`;
    out.push(acc);
  }
  return out;
}

function objectDisplayName(fullKey: string, currentPrefix: string): string {
  if (currentPrefix && fullKey.startsWith(currentPrefix)) {
    return fullKey.slice(currentPrefix.length);
  }
  const i = fullKey.lastIndexOf("/");
  return i === -1 ? fullKey : fullKey.slice(i + 1);
}

function mergeObjectsByKey(existing: S3ObjectEntry[], more: S3ObjectEntry[]): S3ObjectEntry[] {
  const byKey = new Map<string, S3ObjectEntry>();
  for (const o of existing) byKey.set(o.key, o);
  for (const o of more) {
    if (!byKey.has(o.key)) byKey.set(o.key, o);
  }
  return [...byKey.values()];
}

function mergePrefixLists(a: string[], b: string[]): string[] {
  return [...new Set([...a, ...b])];
}

function decodeBucketNameFromRouteParam(encoded: string): string {
  try {
    return decodeURIComponent(encoded);
  } catch {
    return encoded;
  }
}

/** S3 prefix like `logs/2024/` -> URL path segments after `/buckets/:bucket/`. */
function prefixToBrowsePathSegments(prefix: string): string {
  if (!prefix) return "";
  const trimmed = prefix.replace(/\/$/, "").trim();
  if (!trimmed) return "";
  return trimmed
    .split("/")
    .filter(Boolean)
    .map(seg => encodeURIComponent(seg))
    .join("/");
}

/** URL path segments after bucket -> S3 prefix ending with `/`. */
function browsePathSegmentsToPrefix(splat: string | undefined): string {
  if (!splat?.trim()) return "";
  return splat
    .split("/")
    .filter(Boolean)
    .map(seg => {
      try {
        return decodeURIComponent(seg);
      } catch {
        return seg;
      }
    })
    .join("/")
    .concat("/");
}

export default function BucketsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const params = useParams<{ bucketName?: string; "*": string | undefined }>();
  const { bucketName: bucketNameEncoded } = params;
  const browsePath = params["*"];
  const selectedBucketName = bucketNameEncoded
    ? decodeBucketNameFromRouteParam(bucketNameEncoded)
    : null;
  const objectPrefix = browsePathSegmentsToPrefix(browsePath);

  const [phase, setPhase] = useState<"loading" | "ready">("loading");
  const [buckets, setBuckets] = useState<S3BucketRow[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [objectsPhase, setObjectsPhase] = useState<"idle" | "loading" | "ready">("idle");
  const [accumulatedObjects, setAccumulatedObjects] = useState<S3ObjectEntry[]>([]);
  const [accumulatedPrefixes, setAccumulatedPrefixes] = useState<string[]>([]);
  const [listTruncated, setListTruncated] = useState(false);
  const [listNextToken, setListNextToken] = useState<string | undefined>(undefined);
  const [objectsMoreLoading, setObjectsMoreLoading] = useState(false);
  const [objectsError, setObjectsError] = useState<string | null>(null);
  const [fileDetailObject, setFileDetailObject] = useState<S3ObjectEntry | null>(null);
  const [overviewPhase, setOverviewPhase] = useState<"idle" | "loading" | "ready">("idle");
  const [overviewData, setOverviewData] = useState<ObjectOverviewResponse | null>(null);
  const [overviewError, setOverviewError] = useState<string | null>(null);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [bundleExplorerBusy, setBundleExplorerBusy] = useState(false);
  const loadMoreAnchorRef = useRef<HTMLDivElement | null>(null);

  /** Migrate old `?prefix=` links to path-appended URLs. */
  useEffect(() => {
    const legacyPrefix = searchParams.get("prefix");
    if (!selectedBucketName || !legacyPrefix) return;
    const base = `/buckets/${encodeURIComponent(selectedBucketName)}`;
    const segments = prefixToBrowsePathSegments(legacyPrefix);
    navigate(segments ? `${base}/${segments}` : base, { replace: true });
  }, [navigate, searchParams, selectedBucketName]);

  useEffect(() => {
    setFileDetailObject(null);
  }, [selectedBucketName, objectPrefix]);

  useEffect(() => {
    if (!fileDetailObject || !selectedBucketName) {
      setOverviewPhase("idle");
      setOverviewData(null);
      setOverviewError(null);
      return;
    }

    let cancelled = false;
    setOverviewPhase("loading");
    setOverviewData(null);
    setOverviewError(null);

    void (async () => {
      try {
        const data = await getObjectOverview(selectedBucketName, fileDetailObject.key);
        if (cancelled) return;
        setOverviewData(data);
        setOverviewPhase("ready");
      } catch (e) {
        if (cancelled) return;
        setOverviewError(getErrorMessageInternal(e).message);
        setOverviewPhase("ready");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fileDetailObject, selectedBucketName]);

  useEffect(() => {
    setDownloadBusy(false);
    setBundleExplorerBusy(false);
  }, [fileDetailObject, selectedBucketName]);

  useEffect(() => {
    let cancelled = false;
    setPhase("loading");
    setLoadError(null);

    void (async () => {
      try {
        const rows = await listBuckets();
        if (cancelled) return;
        setBuckets(rows);
        setPhase("ready");
      } catch (e) {
        if (cancelled) return;
        setLoadError(getErrorMessageInternal(e).message);
        setPhase("ready");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedBucketName) {
      setObjectsPhase("idle");
      setAccumulatedObjects([]);
      setAccumulatedPrefixes([]);
      setListTruncated(false);
      setListNextToken(undefined);
      setObjectsMoreLoading(false);
      setObjectsError(null);
      return;
    }

    let cancelled = false;
    setObjectsPhase("loading");
    setObjectsError(null);
    setObjectsMoreLoading(false);
    setAccumulatedObjects([]);
    setAccumulatedPrefixes([]);
    setListTruncated(false);
    setListNextToken(undefined);

    void (async () => {
      try {
        const data = await listBucketObjects(selectedBucketName, {
          prefix: objectPrefix || undefined,
        });
        if (cancelled) return;
        setAccumulatedObjects(data.objects);
        setAccumulatedPrefixes(data.commonPrefixes);
        setListTruncated(data.isTruncated ?? false);
        setListNextToken(data.nextContinuationToken ?? data.continuationToken);
        setObjectsPhase("ready");
      } catch (e) {
        if (cancelled) return;
        setObjectsError(getErrorMessageInternal(e).message);
        setObjectsPhase("ready");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedBucketName, objectPrefix]);

  const isEmpty = phase === "ready" && !loadError && buckets.length === 0;
  const showBucketTable = phase === "ready" && !loadError && buckets.length > 0;

  const openBucket = (name: string) => {
    navigate(`/buckets/${encodeURIComponent(name)}`);
  };

  const goToAllBuckets = () => {
    navigate("/buckets");
  };

  const navigateToFolderPrefix = (prefix: string) => {
    if (!selectedBucketName) return;
    const base = `/buckets/${encodeURIComponent(selectedBucketName)}`;
    const segments = prefixToBrowsePathSegments(prefix);
    navigate(segments ? `${base}/${segments}` : base);
  };

  const crumbs = cumulativePrefixes(objectPrefix);

  const canLoadMoreObjects =
    Boolean(selectedBucketName) &&
    listTruncated &&
    listNextToken !== undefined &&
    listNextToken !== "";

  const handleLoadMoreObjects = () => {
    if (!selectedBucketName || !listNextToken || objectsMoreLoading) return;
    void (async () => {
      setObjectsMoreLoading(true);
      setObjectsError(null);
      try {
        const data = await listBucketObjects(selectedBucketName, {
          prefix: objectPrefix || undefined,
          continuationToken: listNextToken,
        });
        setAccumulatedObjects(prev => mergeObjectsByKey(prev, data.objects));
        setAccumulatedPrefixes(prev => mergePrefixLists(prev, data.commonPrefixes));
        setListTruncated(data.isTruncated ?? false);
        setListNextToken(data.nextContinuationToken ?? data.continuationToken);
      } catch (e) {
        setObjectsError(getErrorMessageInternal(e).message);
      } finally {
        setObjectsMoreLoading(false);
      }
    })();
  };

  const handleObjectDownload = () => {
    if (!fileDetailObject || !selectedBucketName) return;
    void (async () => {
      setDownloadBusy(true);
      try {
        await downloadBucketObject(selectedBucketName, fileDetailObject.key, {
          region: overviewData?.region,
        });
      } catch (e) {
        window.alert(getErrorMessageInternal(e).message);
      } finally {
        setDownloadBusy(false);
      }
    })();
  };

  const handleOpenInBundleExplorer = () => {
    if (!fileDetailObject || !selectedBucketName) return;
    if (!showBundleExplorerForS3ObjectKey(fileDetailObject.key)) return;
    void (async () => {
      setBundleExplorerBusy(true);
      try {
        const { downloadUrl } = await fetchBucketObjectDownloadInfo(
          selectedBucketName,
          fileDetailObject.key,
          { region: overviewData?.region }
        );
        const previewUrl = buildPreviewUrlWithPresignedDocumentUrl(
          getBundleExplorerUrl(),
          downloadUrl
        );
        window.open(previewUrl, "_blank", "noopener,noreferrer");
      } catch (e) {
        window.alert(getErrorMessageInternal(e).message);
      } finally {
        setBundleExplorerBusy(false);
      }
    })();
  };

  return (
    <Box>
      <VStack p={10} align="stretch" spacing={6}>
        {!selectedBucketName && (
          <>
            <Box>
              <Heading size="lg">Buckets</Heading>
              <Text mt={2} color="gray.600">
                List of S3 buckets on AWS. Click a bucket to open it and browse objects.
              </Text>
            </Box>

            {phase === "loading" && (
              <Box py={16} textAlign="center">
                <Spinner size="xl" thickness="4px" color="blue.500" />
                <Text mt={4} color="gray.600">
                  Loading buckets…
                </Text>
              </Box>
            )}

            {phase === "ready" && loadError && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <Box>
                  <AlertTitle>Could not load buckets</AlertTitle>
                  <AlertDescription>{loadError}</AlertDescription>
                </Box>
              </Alert>
            )}

            {isEmpty && (
              <Box borderWidth="1px" borderRadius="md" p={8} bg="gray.50">
                <Text color="gray.600">No buckets found.</Text>
              </Box>
            )}

            {showBucketTable && (
              <Table
                id="s3Buckets"
                columns={[...BUCKET_TABLE_COLUMNS]}
                data={buckets.map(b => ({
                  key: b.name,
                  Bucket: (
                    <Text
                      as="span"
                      cursor="pointer"
                      color="blue.600"
                      textDecoration="underline"
                      onClick={() => openBucket(b.name)}
                    >
                      {b.name}
                    </Text>
                  ),
                  Created: formatCreatedAt(b.creationDate),
                }))}
              />
            )}
          </>
        )}

        {selectedBucketName && (
          <>
            <Box>
              <Heading size="lg">Browse bucket</Heading>
              <HStack mt={2} spacing={3} flexWrap="wrap">
                <Button size="sm" variant="outline" onClick={goToAllBuckets}>
                  ← All buckets
                </Button>
                {objectsPhase === "ready" && !objectsError && canLoadMoreObjects ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      loadMoreAnchorRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      })
                    }
                  >
                    Load more objects
                  </Button>
                ) : null}
              </HStack>
            </Box>

            <Box overflowX="auto">
              <Breadcrumb fontSize="sm" separator="/">
                <BreadcrumbItem isCurrentPage={!objectPrefix}>
                  {objectPrefix ? (
                    <BreadcrumbLink
                      onClick={e => {
                        e.preventDefault();
                        navigateToFolderPrefix("");
                      }}
                    >
                      {selectedBucketName}
                    </BreadcrumbLink>
                  ) : (
                    <Text fontWeight="semibold">{selectedBucketName}</Text>
                  )}
                </BreadcrumbItem>
                {crumbs.map((cuml, idx) => (
                  <BreadcrumbItem key={cuml} isCurrentPage={idx === crumbs.length - 1}>
                    {idx === crumbs.length - 1 ? (
                      <Text fontWeight="semibold">{breadcrumbFolderLabel(cuml)}</Text>
                    ) : (
                      <BreadcrumbLink
                        onClick={e => {
                          e.preventDefault();
                          navigateToFolderPrefix(cuml);
                        }}
                      >
                        {breadcrumbFolderLabel(cuml)}
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                ))}
              </Breadcrumb>
            </Box>

            {objectsPhase === "loading" && (
              <Box py={8} textAlign="center">
                <Spinner size="lg" color="blue.500" />
                <Text mt={3} color="gray.600">
                  Loading objects…
                </Text>
              </Box>
            )}

            {objectsPhase === "ready" && objectsError && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <Box>
                  <AlertTitle>Could not load objects</AlertTitle>
                  <AlertDescription>{objectsError}</AlertDescription>
                </Box>
              </Alert>
            )}

            {objectsPhase === "ready" && !objectsError && (
              <VStack align="stretch" spacing={6}>
                {listTruncated && !canLoadMoreObjects ? (
                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription>
                      Listing is truncated but no continuation token was returned; load more is not
                      available.
                    </AlertDescription>
                  </Alert>
                ) : null}

                {(() => {
                  const prefixesSorted = [...accumulatedPrefixes].sort((a, b) =>
                    folderDisplayName(a).localeCompare(folderDisplayName(b))
                  );
                  const objectsSorted = [...accumulatedObjects].sort((a, b) =>
                    objectDisplayName(a.key, objectPrefix).localeCompare(
                      objectDisplayName(b.key, objectPrefix)
                    )
                  );
                  const folderRows = prefixesSorted.map(p => ({
                    key: `folder:${p}`,
                    Name: (
                      <Text
                        as="span"
                        cursor="pointer"
                        color="blue.600"
                        textDecoration="underline"
                        onClick={() => navigateToFolderPrefix(p)}
                      >
                        {folderDisplayName(p)}
                      </Text>
                    ),
                    Size: "—",
                    "Last modified": "—",
                  }));
                  const fileRows = objectsSorted.map(obj => ({
                    key: `file:${obj.key}`,
                    Name: (
                      <Text
                        as="span"
                        cursor="pointer"
                        color="blue.600"
                        textDecoration="underline"
                        onClick={() => setFileDetailObject(obj)}
                      >
                        {objectDisplayName(obj.key, objectPrefix)}
                      </Text>
                    ),
                    Size: formatSize(obj.size),
                    "Last modified": obj.lastModified ? formatCreatedAt(obj.lastModified) : "—",
                  }));
                  const rows = [...folderRows, ...fileRows];
                  if (rows.length === 0) {
                    return <Text color="gray.600">This folder is empty.</Text>;
                  }
                  return (
                    <Table
                      id="s3BucketContents"
                      columns={[...BUCKET_CONTENT_COLUMNS]}
                      data={rows}
                    />
                  );
                })()}

                {canLoadMoreObjects ? (
                  <Box ref={loadMoreAnchorRef} scrollMarginTop="1rem">
                    <HStack spacing={3} flexWrap="wrap">
                      <Button
                        size="sm"
                        colorScheme="blue"
                        onClick={handleLoadMoreObjects}
                        isLoading={objectsMoreLoading}
                        loadingText="Loading…"
                      >
                        Next
                      </Button>
                      <Text fontSize="sm" color="gray.600">
                        More results under this directory; loads the next page into the table.
                      </Text>
                    </HStack>
                  </Box>
                ) : null}
              </VStack>
            )}
          </>
        )}
      </VStack>

      <Modal
        isOpen={fileDetailObject !== null && selectedBucketName !== null}
        onClose={() => setFileDetailObject(null)}
        size="2xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Object details</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {fileDetailObject && selectedBucketName ? (
              overviewPhase === "loading" ? (
                <Box py={8} textAlign="center">
                  <Spinner size="lg" color="blue.500" />
                  <Text mt={3} color="gray.600">
                    Loading details…
                  </Text>
                </Box>
              ) : overviewError ? (
                <VStack align="stretch" spacing={4}>
                  <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <AlertTitle>Could not load object details</AlertTitle>
                      <AlertDescription>{overviewError}</AlertDescription>
                    </Box>
                  </Alert>
                  <HStack justify="flex-end" spacing={2}>
                    {showBundleExplorerForS3ObjectKey(fileDetailObject.key) ? (
                      <Button
                        colorScheme="blue"
                        variant="outline"
                        rightIcon={<FiExternalLink />}
                        isLoading={bundleExplorerBusy}
                        loadingText="Opening…"
                        isDisabled={downloadBusy}
                        onClick={handleOpenInBundleExplorer}
                      >
                        Open in Bundle Explorer
                      </Button>
                    ) : null}
                    <Button
                      colorScheme="blue"
                      rightIcon={<FiDownload />}
                      isLoading={downloadBusy}
                      loadingText="Downloading…"
                      isDisabled={bundleExplorerBusy}
                      onClick={handleObjectDownload}
                    >
                      Download file
                    </Button>
                  </HStack>
                </VStack>
              ) : overviewData ? (
                <VStack align="stretch" spacing={4}>
                  <Grid w="100%" templateColumns="max-content 1fr" columnGap={10} rowGap={3}>
                    {overviewDetailRows(overviewData, fileDetailObject).flatMap(
                      ([label, value]) => [
                        <Text
                          key={`${label}-l`}
                          fontWeight="semibold"
                          color="gray.600"
                          whiteSpace="nowrap"
                        >
                          {label}
                        </Text>,
                        <Text key={`${label}-v`} wordBreak="break-all" minW={0}>
                          {value}
                        </Text>,
                      ]
                    )}
                  </Grid>
                  <HStack justify="flex-end" spacing={2}>
                    {showBundleExplorerForS3ObjectKey(fileDetailObject.key) ? (
                      <Button
                        colorScheme="blue"
                        variant="outline"
                        rightIcon={<FiExternalLink />}
                        isLoading={bundleExplorerBusy}
                        loadingText="Opening…"
                        isDisabled={downloadBusy}
                        onClick={handleOpenInBundleExplorer}
                      >
                        Open in Bundle Explorer
                      </Button>
                    ) : null}
                    <Button
                      colorScheme="blue"
                      rightIcon={<FiDownload />}
                      isLoading={downloadBusy}
                      loadingText="Downloading…"
                      isDisabled={bundleExplorerBusy}
                      onClick={handleObjectDownload}
                    >
                      Download file
                    </Button>
                  </HStack>
                </VStack>
              ) : null
            ) : null}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
